/**
 * Venttra - Authors collection schema
 */
import { buildCollection } from "@firecms/core";
import { editorOrAdminPermissions } from "../permissions";
import { createAuthorsLocalizationReviewView } from "../components/LocalizationReviewView";
import { CONTENT_LOCALES, LOCALE_LABELS } from "../constants";
import { collectAuthorCompletenessIssues } from "../lib/completenessValidation";
import { CONTENT_STATUS_ENUM } from "../lib/contentStatus";
import {
  STATUS_FIELD_DESCRIPTION,
  publishedSnapshotProperties,
} from "../lib/publishedSnapshotProperties";
import { createStatusPreSave } from "../lib/statusPreSave";

export const authorsCollection = buildCollection({
  id: "authors",
  name: "Authors",
  singularName: "Author",
  path: "authors",
  icon: "Person",
  group: "Content",
  permissions: editorOrAdminPermissions,
  entityViews: [createAuthorsLocalizationReviewView()],
  callbacks: {
    onPreSave: createStatusPreSave({
      collectCompleteness: collectAuthorCompletenessIssues,
      entityLabel: "author",
    }),
  },
  properties: {
    name: {
      name: "Name",
      dataType: "string",
      validation: { required: true },
    },
    bio: {
      name: "Bio",
      dataType: "map",
      description: "Localized biography: { en, de, fr, it }",
      properties: Object.fromEntries(
        CONTENT_LOCALES.map((locale) => [
          locale,
          { name: LOCALE_LABELS[locale], dataType: "string" as const, markdown: true },
        ])
      ),
    },
    slug: {
      name: "Slug",
      dataType: "string",
      description: "URL-safe slug (e.g. amelia-hayes)",
      validation: { required: true },
    },
    status: {
      name: "Status",
      dataType: "string",
      enumValues: CONTENT_STATUS_ENUM,
      description: STATUS_FIELD_DESCRIPTION,
    },
    image_url: {
      name: "Image URL",
      dataType: "string",
      description: "Profile image URL (optional)",
    },
    join_date: {
      name: "Join Date",
      dataType: "string",
      description: "e.g. 2022",
    },
    based_in: {
      name: "Based in",
      dataType: "string",
    },
    focus_tags: {
      name: "Focus tags",
      dataType: "array",
      of: { dataType: "string" },
    },
    social_links: {
      name: "Social links",
      dataType: "map",
      properties: {
        website: { name: "Website", dataType: "string" },
        instagram: { name: "Instagram", dataType: "string" },
      },
    },
    tier_level: {
      name: "Tier level",
      dataType: "number",
    },
    tier_label: {
      name: "Tier label",
      dataType: "string",
    },
    user_id: {
      name: "Member user ID",
      dataType: "string",
      description: "Firebase UID when this author is linked to a member account",
      readOnly: true,
    },
    ...publishedSnapshotProperties,
    created_at: {
      name: "Created At",
      dataType: "date",
      autoValue: "on_create",
    },
    updated_at: {
      name: "Updated At",
      dataType: "date",
      autoValue: "on_update",
    },
  },
});
